import type { WorkOrderStatus, WorkStepStatus } from "@paperclip-cloud/shared";
import { api } from "../client";

export interface WorkStepRow {
  id: string;
  work_order_id: string;
  device_id: string | null;
  device_name?: string;
  agent_role: string;
  instruction: string;
  status: WorkStepStatus;
  started_at: number | null;
  ended_at: number | null;
  /** Set on the source step after a transfer; points at the step that resumed. */
  transferred_to_step_id?: string | null;
  /** Set on the new step; points back at the source step it took over from. */
  resumed_from_step_id?: string | null;
  /** Blob holding the source's checkpoint payload (issue body + comments + work products). */
  checkpoint_blob_id?: string | null;
  result?: {
    outputs?: Array<
      | { kind: "inline"; name: string; mime: string; data: string }
      | { kind: "blob"; name: string; mime: string; blob_id: string; bytes: number }
      | { kind: "text"; name: string; text: string }
    >;
    error?: { code: string; message: string };
  };
}

export interface WorkOrderRow {
  id: string;
  company_id: string;
  requested_by_user_id: string;
  requested_by_email?: string;
  title: string;
  description: string;
  status: WorkOrderStatus;
  ceo_session_id: string | null;
  created_at: number;
  updated_at: number;
}

export interface WorkOrderDetail extends WorkOrderRow {
  steps: WorkStepRow[];
  ceo_excerpt: Array<{ role: "user" | "ceo"; content: string; at: number }>;
}

export const workOrdersApi = {
  list: (companyId: string, opts: { limit?: number; status?: WorkOrderStatus } = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.status) params.set("status", opts.status);
    const qs = params.toString();
    return api<{ orders: WorkOrderRow[] }>(
      `/v1/companies/${companyId}/work-orders${qs ? `?${qs}` : ""}`,
    );
  },
  get: (id: string) => api<WorkOrderDetail>(`/v1/work-orders/${id}`),
  cancel: (id: string) =>
    api<WorkOrderRow>(`/v1/work-orders/${id}/cancel`, { method: "POST" }),
  blobUrl: (blobId: string) => `/v1/blobs/${blobId}`,
};

/**
 * Move a running work step to another device. The source's local Paperclip
 * progress (issue body + comments + work products) is checkpointed and
 * uploaded as a blob; the target then resumes with that context prepended.
 *
 * The HTTP turn blocks for the source-side checkpoint (default cap: 30 s).
 * Returns the new step id; watch the WS for `work_order.update` /
 * `exec.update` envelopes on it.
 */
export const workStepsApi = {
  transfer: (
    sourceStepId: string,
    target_device_id: string,
    reason?: string,
  ) =>
    api<{
      source_work_step_id: string;
      new_work_step_id: string;
      checkpoint_blob_id: string | null;
    }>(`/v1/work-steps/${sourceStepId}/transfer`, {
      method: "POST",
      body: { target_device_id, reason },
    }),
};
